/**
 * SkippedRowsViewer Component
 *
 * Displays tables with skipped rows and allows downloading CSV files
 */

import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Download, Eye, Loader2, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { format } from "date-fns";
import { buildApiUrl } from "@/lib/api";

interface SkippedRow {
  runId: string;
  pipelineName: string;
  tableName: string;
  rowsSkipped: number;
  rowsRead: number;
  rowsCopied: number;
  skipPercentage: number;
  runStart: string;
  blobPath: string;
  blobSize?: number;
  blobExists?: boolean;
}

interface SkippedRowsViewerProps {
  data?: {
    total: number;
    tables: number;
    byTable: Record<string, SkippedRow[]>;
    all: SkippedRow[];
  };
  isLoading: boolean;
}

export function SkippedRowsViewer({ data, isLoading }: SkippedRowsViewerProps) {
  const [previewData, setPreviewData] = useState<any>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);

  const downloadSkippedRows = async (blobPath: string, tableName: string) => {
    try {
      const response = await fetch(
        buildApiUrl(`/api/adf/skipped-rows/download?blobPath=${encodeURIComponent(blobPath)}`)
      );

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${tableName}_skipped_rows.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file');
    }
  };

  const previewSkippedRows = async (blobPath: string) => {
    setIsLoadingPreview(true);
    setPreviewError(null);
    setPreviewData(null);

    try {
      const response = await fetch(
        buildApiUrl(`/api/adf/skipped-rows/preview?blobPath=${encodeURIComponent(blobPath)}&maxRows=10`)
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to preview file');
      }

      const data = await response.json();
      setPreviewData(data);
    } catch (error) {
      console.error('Preview error:', error);
      setPreviewError(error instanceof Error ? error.message : 'Failed to load preview');
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <Alert>
        <AlertDescription>
          No skipped rows found in the selected time range. All data was copied successfully!
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Summary Card */}
        <Card>
          <CardHeader>
            <CardTitle>Skipped Rows Summary</CardTitle>
            <CardDescription>
              {data.total} instance{data.total !== 1 ? 's' : ''} of skipped rows across {data.tables} table{data.tables !== 1 ? 's' : ''}
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Grouped by Table */}
        {Object.entries(data.byTable).map(([tableName, rows]) => {
          const totalSkipped = rows.reduce((sum, r) => sum + r.rowsSkipped, 0);
          const avgSkipPercentage = rows.reduce((sum, r) => sum + r.skipPercentage, 0) / rows.length;

          return (
            <Card key={tableName}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{tableName}</span>
                  <Badge variant="secondary">
                    {totalSkipped.toLocaleString()} rows skipped
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Average skip rate: {avgSkipPercentage.toFixed(2)}%
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Run Time</TableHead>
                        <TableHead className="text-right">Rows Read</TableHead>
                        <TableHead className="text-right">Rows Copied</TableHead>
                        <TableHead className="text-right">Rows Skipped</TableHead>
                        <TableHead className="text-right">Skip %</TableHead>
                        <TableHead className="text-right">File Size</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((row, idx) => (
                        <TableRow key={`${row.runId}-${idx}`}>
                          <TableCell>{format(new Date(row.runStart), 'MMM d, HH:mm:ss')}</TableCell>
                          <TableCell className="text-right">{row.rowsRead.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-green-600">{row.rowsCopied.toLocaleString()}</TableCell>
                          <TableCell className="text-right text-orange-600 font-medium">{row.rowsSkipped.toLocaleString()}</TableCell>
                          <TableCell className="text-right">
                            <Badge variant={row.skipPercentage > 5 ? "destructive" : "secondary"}>
                              {row.skipPercentage.toFixed(2)}%
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right text-muted-foreground">
                            {row.blobSize ? formatBytes(row.blobSize) : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              {row.blobExists && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => previewSkippedRows(row.blobPath)}
                                  >
                                    <Eye className="h-4 w-4 mr-1" />
                                    Preview
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => downloadSkippedRows(row.blobPath, row.tableName)}
                                  >
                                    <Download className="h-4 w-4 mr-1" />
                                    Download
                                  </Button>
                                </>
                              )}
                              {!row.blobExists && (
                                <Badge variant="outline" className="text-muted-foreground">
                                  File not found
                                </Badge>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Preview Dialog */}
      <Dialog open={previewData !== null || isLoadingPreview} onOpenChange={(open) => {
        if (!open) {
          setPreviewData(null);
          setPreviewError(null);
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Skipped Rows Preview</DialogTitle>
            <DialogDescription>
              {previewData && previewData.totalRows !== null && `Showing ${previewData.previewRows} of ${previewData.totalRows} skipped rows`}
              {previewData && previewData.totalRows === null && `Showing first ${previewData.previewRows} rows (large file preview)`}
            </DialogDescription>
          </DialogHeader>

          {isLoadingPreview && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {previewError && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{previewError}</AlertDescription>
            </Alert>
          )}

          {previewData && previewData.rows && (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    {Object.keys(previewData.rows[0] || {}).map((header) => (
                      <TableHead key={header}>{header}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {previewData.rows.map((row: any, idx: number) => (
                    <TableRow key={idx}>
                      {Object.values(row).map((value: any, cellIdx: number) => (
                        <TableCell key={cellIdx} className="max-w-xs truncate">
                          {String(value)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {previewData && previewData.totalRows !== null && previewData.totalRows > previewData.previewRows && (
            <Alert>
              <AlertDescription>
                Showing first {previewData.previewRows} rows. Download the full CSV to see all {previewData.totalRows} rows.
              </AlertDescription>
            </Alert>
          )}

          {previewData && previewData.totalRows === null && (
            <Alert>
              <AlertDescription>
                This is a large file preview (only first 100KB loaded). Download the full CSV to see all rows.
              </AlertDescription>
            </Alert>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
